// src/App.jsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PageLoader } from './components/common/UI';
import './styles/globals.css';

// Lazy load every page — this isolates any broken export to that page only
const AppLayout = lazy(() => import('./components/layout/AppLayout'));
const Login     = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Animals   = lazy(() => import('./pages/Animals/Animals'));
const Feeds     = lazy(() => import('./pages/Feeds/Feeds'));
const Health    = lazy(() => import('./pages/Health/Health'));
const Breeding  = lazy(() => import('./pages/Breeding/Breeding'));
const Finance   = lazy(() => import('./pages/Finance/Finance'));
const Settings  = lazy(() => import('./pages/Settings/Settings'));

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user)   return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user)    return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '13px',
              borderRadius: '10px',
            },
            success: {
              iconTheme: { primary: '#276840', secondary: '#fff' },
            },
          }}
        />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <AppLayout />
                </PrivateRoute>
              }
            >
              <Route index             element={<Dashboard />} />
              <Route path="animals"   element={<Animals />} />
              <Route path="feeds"     element={<Feeds />} />
              <Route path="health"    element={<Health />} />
              <Route path="breeding"  element={<Breeding />} />
              <Route path="finance"   element={<Finance />} />
              <Route path="settings"  element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}